// Template-aware structural diff. Compares a pasted payload against the canonical
// template by element/key PATHS (not text lines), so we can say precisely:
//   - which tags/keys the template expects but the payload is missing,
//   - which tags/keys the payload has that the template does not,
//   - which expected leaves are present but empty ("missing values").
// XML namespaces are stripped (templates are namespace-free); repeated siblings and
// array items collapse to one path, so we compare shape, not instance counts.

import { detectFormat, PayloadFormat } from './payloadFormat';

export interface StructuralDiffResult {
  comparable: boolean;
  templateFormat: PayloadFormat;
  candidateFormat: PayloadFormat;
  missing: string[]; // in template, not in payload (top-most node per branch)
  extra: string[]; // in payload, not in template (top-most node per branch)
  emptyValues: string[]; // expected leaves that are present but blank in the payload
}

interface Flattened {
  paths: Set<string>; // every element/attribute/key path
  emptyLeaves: Set<string>; // leaf paths whose value is blank (and never non-blank)
}

const parentOf = (path: string): string => {
  const idx = path.lastIndexOf('/');
  return idx === -1 ? '' : path.slice(0, idx);
};

// Keep only the top-most differing node per branch (its parent exists on the other side).
const topmost = (diff: string[], otherPaths: Set<string>): string[] =>
  diff.filter((p) => {
    const parent = parentOf(p);
    return parent === '' || otherPaths.has(parent);
  }).sort();

// ---------- XML ----------

const flattenXml = (
  el: Element,
  prefix: string,
  paths: Set<string>,
  empties: Set<string>,
  valued: Set<string>
): void => {
  const name = el.localName || el.tagName;
  const path = prefix ? `${prefix}/${name}` : name;
  paths.add(path);

  Array.from(el.attributes)
    .filter((a) => a.name !== 'xmlns' && !a.name.startsWith('xmlns:'))
    .forEach((a) => {
      const ap = `${path}/@${a.localName || a.name}`;
      paths.add(ap);
      (a.value.trim() ? valued : empties).add(ap);
    });

  const children = Array.from(el.childNodes).filter((n) => n.nodeType === 1) as Element[];
  if (children.length === 0) {
    const text = (el.textContent ?? '').trim();
    (text ? valued : empties).add(path);
  } else {
    children.forEach((c) => flattenXml(c, path, paths, empties, valued));
  }
};

// ---------- JSON ----------

const walkJson = (
  node: any,
  path: string,
  paths: Set<string>,
  empties: Set<string>,
  valued: Set<string>
): void => {
  if (Array.isArray(node)) {
    const ap = path ? `${path}/[]` : '[]';
    paths.add(ap);
    node.forEach((item) => walkJson(item, ap, paths, empties, valued));
  } else if (node && typeof node === 'object') {
    Object.entries(node).forEach(([k, v]) => {
      const cp = path ? `${path}/${k}` : k;
      paths.add(cp);
      walkJson(v, cp, paths, empties, valued);
    });
  } else {
    const s = node === null || node === undefined ? '' : String(node);
    (s.trim() ? valued : empties).add(path);
  }
};

const flatten = (payload: string, format: PayloadFormat): Flattened | null => {
  const paths = new Set<string>();
  const empties = new Set<string>();
  const valued = new Set<string>();

  try {
    if (format === 'xml') {
      const doc = new DOMParser().parseFromString(payload, 'application/xml');
      if (doc.getElementsByTagName('parsererror').length > 0 || !doc.documentElement) return null;
      flattenXml(doc.documentElement, '', paths, empties, valued);
    } else if (format === 'json') {
      walkJson(JSON.parse(payload), '', paths, empties, valued);
    } else {
      return null;
    }
  } catch {
    return null;
  }

  // A path is only "empty" if it was never seen with a value (handles collapsed repeats).
  const emptyLeaves = new Set<string>(Array.from(empties).filter((p) => !valued.has(p)));
  return { paths, emptyLeaves };
};

export const structuralDiff = (candidate: string, template: string): StructuralDiffResult => {
  const candidateFormat = detectFormat(candidate);
  const templateFormat = detectFormat(template);

  const empty: StructuralDiffResult = {
    comparable: false,
    templateFormat,
    candidateFormat,
    missing: [],
    extra: [],
    emptyValues: [],
  };

  if (candidateFormat === 'unknown' || templateFormat === 'unknown' || candidateFormat !== templateFormat) {
    return empty;
  }

  const cand = flatten(candidate, candidateFormat);
  const tmpl = flatten(template, templateFormat);
  if (!cand || !tmpl) return empty;

  const missing = topmost(Array.from(tmpl.paths).filter((p) => !cand.paths.has(p)), cand.paths);
  const extra = topmost(Array.from(cand.paths).filter((p) => !tmpl.paths.has(p)), tmpl.paths);
  const emptyValues = Array.from(cand.emptyLeaves).filter((p) => tmpl.paths.has(p)).sort();

  return {
    comparable: true,
    templateFormat,
    candidateFormat,
    missing,
    extra,
    emptyValues,
  };
};

export const isMatch = (r: StructuralDiffResult): boolean =>
  r.comparable && r.missing.length === 0 && r.extra.length === 0 && r.emptyValues.length === 0;
